import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RoomBackdrop } from '../components/RoomBackdrop';
import './SimplePages.css';

/** Inquiries from Let’s Talk go here */
export const CONTACT_EMAIL = 'darryl.goss@gossintl.com';

const NEED_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'transform', label: 'Transform' },
  { value: 'govern', label: 'Govern' },
  { value: 'unsure', label: 'Not sure' },
];

function interestToNeed(interest) {
  if (!interest) return 'lead';
  const slug = interest.toLowerCase();
  if (slug.includes('lead') || slug.includes('coach') || slug.includes('fractional')) return 'lead';
  if (slug.includes('transform') || slug.includes('turnaround') || slug.includes('ops')) return 'transform';
  if (slug.includes('govern') || slug.includes('board') || slug.includes('speak')) return 'govern';
  return 'unsure';
}

function buildMailto({ name, company, need, message, replyEmail }) {
  const needLabel = NEED_OPTIONS.find((o) => o.value === need)?.label || need;
  const subject = `1 Goss Consulting inquiry — ${name || 'Website'}`;
  const body = [
    `Name: ${name}`,
    `Company: ${company}`,
    replyEmail ? `Reply-to: ${replyEmail}` : null,
    `Need: ${needLabel}`,
    '',
    message || '(No message)',
    '',
    '— Sent from 1gossconsulting.com Let’s Talk',
  ]
    .filter((line) => line !== null)
    .join('\n');

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function PartnersPage() {
  return (
    <>
      <RoomBackdrop room="partners" dim={0} fit="contain" />
      <main className="page page--scroll simple">
        <header className="simple__header">
          <h1 className="page-title">What our partners say about us.</h1>
          <p className="body-lg">
            Voices from leaders and partners who have worked alongside 1 Goss Consulting —
            on engagements that demand clarity, steadiness, and results.
          </p>
        </header>
        <div className="simple__stack" aria-label="Partner testimonials">
          <article className="simple-card partners-placeholder">
            <p className="quote">“A partner quote will live here.”</p>
            <p className="quote-attr">Name · Role · Organization</p>
          </article>
          <article className="simple-card partners-placeholder" aria-hidden>
            <p className="quote">“A second voice — referral, board colleague, or engagement partner.”</p>
            <p className="quote-attr">Name · Role · Organization</p>
          </article>
        </div>
        <Link to="/contact" className="simple__cta">
          Introduced by someone we trust? Let&apos;s talk.
        </Link>
      </main>
    </>
  );
}

export function ContactPage() {
  const [params] = useSearchParams();
  const defaultNeed = useMemo(
    () => interestToNeed(params.get('interest') || params.get('need')),
    [params]
  );

  return (
    <>
      <RoomBackdrop room="contact" dim={0} fit="contain" />
      <main className="contact-page">
        <article className="contact-panel">
          <header className="contact-panel__header">
            <h1 className="contact-panel__title">Let&apos;s talk.</h1>
            <p className="contact-panel__lede">
              Inquiries go to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              {' · '}
              <a href="tel:+15126954094">+1 (512) 695-4094</a>
            </p>
          </header>

          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              const href = buildMailto({
                name: String(data.get('name') || '').trim(),
                company: String(data.get('company') || '').trim(),
                need: String(data.get('need') || 'lead'),
                message: String(data.get('message') || '').trim(),
                replyEmail: String(data.get('email') || '').trim(),
              });
              window.location.href = href;
            }}
          >
            <div className="contact-form__row">
              <label>
                Name
                <input name="name" required autoComplete="name" />
              </label>
              <label>
                Email
                <input name="email" type="email" required autoComplete="email" />
              </label>
            </div>
            <div className="contact-form__row">
              <label>
                Company
                <input name="company" required autoComplete="organization" />
              </label>
              <label>
                Need
                <select name="need" defaultValue={defaultNeed}>
                  {NEED_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Message
              <textarea name="message" rows={3} placeholder="What are you working through?" />
            </label>
            <div className="contact-form__actions">
              <button type="submit">Send inquiry</button>
              <p className="contact-form__note">
                Opens your mail app to {CONTACT_EMAIL}.
              </p>
            </div>
          </form>
        </article>
      </main>
    </>
  );
}
